'use client';
import * as React from 'react';
import { styled } from '@mui/material/styles';
import {
  getDataGridUtilityClass,
  gridFocusCellSelector,
  gridFocusColumnHeaderSelector,
  useGridEvent,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import type { GridEventListener, GridRowId } from '@mui/x-data-grid-pro';
import { isRedoShortcut, isUndoShortcut } from '@mui/x-data-grid-pro/internals';
import composeClasses from '@mui/utils/composeClasses';
import useId from '@mui/utils/useId';
import type { RefObject } from '@mui/x-internals/types';
import { useGridPrivateApiContext } from '../../hooks/utils/useGridPrivateApiContext';
import { useGridRootProps } from '../../hooks/utils/useGridRootProps';
import type { DataGridPremiumProcessedProps } from '../../models/dataGridPremiumProps';
import type { GridPrivateApiPremium } from '../../models/gridApiPremium';
import { GridSidebarValue } from '../../hooks/features/sidebar/gridSidebarInterfaces';
import { gridSidebarStateSelector } from '../../hooks/features/sidebar/gridSidebarSelector';
import {
  gridComputedColumnDefinitionSelector,
  gridComputedColumnsSelector,
} from '../../hooks/features/computedColumns/gridComputedColumnsSelectors';
import { isComputedColumnEditable } from '../../hooks/features/computedColumns/gridComputedColumnsUtils';
import {
  registerFormulaFocusSafeElement,
  unregisterFormulaFocusSafeElement,
} from '../../hooks/features/formula/gridFormulaBarElements';
import { GridComputedColumnsPanelHeader } from './GridComputedColumnsPanelHeader';
import { GridComputedColumnsPanelList } from './GridComputedColumnsPanelList';
import { GridComputedColumnsPanelEditor } from './GridComputedColumnsPanelEditor';
import type {
  GridComputedColumnsPanelEditorOutcome,
  GridComputedColumnsPanelEditorSession,
} from './GridComputedColumnsPanelEditor';

type OwnerState = DataGridPremiumProcessedProps;

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['computedColumnsPanel'],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

const GridComputedColumnsPanelRoot = styled('div', {
  name: 'MuiDataGrid',
  slot: 'ComputedColumnsPanel',
})<{ ownerState: OwnerState }>({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
});

type PanelView =
  | {
      kind: 'list';
      /**
       * The item to focus when the list shows (the column just applied); `null`
       * focuses the Add button.
       */
      focusField?: string | null;
    }
  | {
      kind: 'editor';
      /**
       * Bumped for every editor session: a new session remounts the editor with a fresh draft.
       */
      key: number;
      session: GridComputedColumnsPanelEditorSession;
      /**
       * Where the session came from: a request (column menu, cell gesture,
       * `showComputedColumnEditor()`) closes the panel when the editor is done, the
       * list takes the editor back to it.
       */
      origin: 'request' | 'list';
    };

/**
 * What to focus when the panel closes: the element that opened it (the
 * toolbar trigger, through the sidebar's `labelId`), else the grid focus at
 * the time it opened (the column header of the menu — `hideColumnMenu` focuses
 * it — or the cell of a gesture).
 */
type PanelOpener =
  | { kind: 'element'; id: string }
  | { kind: 'columnHeader'; field: string }
  | { kind: 'cell'; id: GridRowId; field: string }
  | null;

function captureOpener(apiRef: RefObject<GridPrivateApiPremium>): PanelOpener {
  const { labelId } = gridSidebarStateSelector(apiRef);
  if (labelId) {
    return { kind: 'element', id: labelId };
  }
  const columnHeader = gridFocusColumnHeaderSelector(apiRef);
  if (columnHeader !== null) {
    return { kind: 'columnHeader', field: columnHeader.field };
  }
  const cell = gridFocusCellSelector(apiRef);
  if (cell !== null) {
    return { kind: 'cell', id: cell.id, field: cell.field };
  }
  return null;
}

function focusOpener(apiRef: RefObject<GridPrivateApiPremium>, opener: PanelOpener): void {
  if (opener === null) {
    return;
  }
  const doc = apiRef.current.rootElementRef?.current?.ownerDocument ?? document;
  switch (opener.kind) {
    case 'element':
      doc.getElementById(opener.id)?.focus();
      break;
    case 'columnHeader':
      if (apiRef.current.getColumn(opener.field) !== undefined) {
        apiRef.current.setColumnHeaderFocus(opener.field);
        apiRef.current.getColumnHeaderElement(opener.field)?.focus();
      }
      break;
    case 'cell':
      if (
        apiRef.current.getRow(opener.id) != null &&
        apiRef.current.getColumn(opener.field) !== undefined
      ) {
        apiRef.current.setCellFocus(opener.id, opener.field);
        // The cell's own focus effect yields to the panel (a focus-safe
        // element still holding the focus), so the element is focused directly.
        apiRef.current.getCellElement(opener.id, opener.field)?.focus();
      }
      break;
    default:
      break;
  }
}

const isTextField = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  ((target.tagName === 'INPUT' && !(target as HTMLInputElement).readOnly) ||
    target.tagName === 'TEXTAREA' ||
    target.closest('[contenteditable]:not([contenteditable="false"])') !== null);

/**
 * The grid's undo/redo shortcut is a native listener on the grid root, which the panel
 * sits in. Typed in one of the panel's text fields, Ctrl+Z is the field's own undo: the
 * shortcut stops here (without `preventDefault`, so the browser performs it) instead of
 * undoing a grid step. From anywhere else in the panel it reaches the grid.
 */
function keepHistoryShortcutsInTextFields(event: KeyboardEvent) {
  const keyboardEvent = event as unknown as React.KeyboardEvent;
  if (
    (isUndoShortcut(keyboardEvent) || isRedoShortcut(keyboardEvent)) &&
    isTextField(event.target)
  ) {
    event.stopPropagation();
  }
}

/**
 * Reads the request left by `showComputedColumnEditor()`. Without a request (the
 * sidebar opened through `showSidebar()`, e.g. the toolbar trigger), the panel shows
 * the list.
 *
 * Reading leaves the request in place: the committed panel clears it in an effect
 * (see `GridComputedColumnsPanel`). This runs from a state initializer and from a
 * `sidebarOpen` listener, and under React 18 StrictMode neither belongs to the
 * committed panel for sure. StrictMode renders the mount twice, from a fresh hook
 * state the second time, so the first initializer call is thrown away, and the
 * `useGridEvent` subscription that first render made outlives it: its listener keeps
 * firing with the closures of that render. A request consumed there never reaches
 * the live panel, which shows the list instead of the editor.
 */
function readEditorRequest(apiRef: RefObject<GridPrivateApiPremium>, key: number): PanelView {
  const request = apiRef.current.caches.computedColumns.editorRequest;
  if (request === null) {
    return { kind: 'list' };
  }
  const definition =
    request.field == null ? null : gridComputedColumnDefinitionSelector(apiRef, request.field);
  return {
    kind: 'editor',
    key,
    origin: 'request',
    session: {
      definition,
      sampleRowId: request.sampleRowId,
      columnIndex: request.columnIndex,
    },
  };
}

/**
 * The computed columns side panel, rendered by the formula feature for
 * `GridSidebarValue.ComputedColumns`: the list of the stored definitions, and
 * the editor view that creates or edits one of them.
 */
function GridComputedColumnsPanel() {
  const apiRef = useGridPrivateApiContext();
  const rootProps = useGridRootProps();
  const classes = useUtilityClasses(rootProps);
  const titleId = useId();

  const [view, setView] = React.useState<PanelView>(() => readEditorRequest(apiRef, 0));
  const editorKeyRef = React.useRef(0);
  const nextEditorKey = () => {
    editorKeyRef.current += 1;
    return editorKeyRef.current;
  };
  const openerRef = React.useRef<PanelOpener>(captureOpener(apiRef));

  // `showSidebar()` publishes `sidebarOpen` on every call, including when the
  // panel is already open: that is how a new request reaches a mounted panel.
  const handleSidebarOpen = React.useCallback<GridEventListener<'sidebarOpen'>>(
    ({ value }) => {
      if (value === GridSidebarValue.ComputedColumns) {
        setView(readEditorRequest(apiRef, nextEditorKey()));
      }
    },
    [apiRef],
  );
  useGridEvent(apiRef, 'sidebarOpen', handleSidebarOpen);

  // The request is consumed once the view built from it is committed, by the panel
  // instance React kept — see `readEditorRequest` for why the reads cannot clear it.
  // A request is read synchronously by the call that leaves it, so nothing is pending
  // when the effect runs: clearing on every view change is safe.
  React.useEffect(() => {
    apiRef.current.caches.computedColumns.editorRequest = null;
  }, [apiRef, view]);

  // Interactions inside the panel must not clear the grid's cell focus — the
  // registry the formula bar uses (see `gridFormulaBarElements`).
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const handleRootRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (rootRef.current) {
        unregisterFormulaFocusSafeElement(apiRef, rootRef.current);
        rootRef.current.removeEventListener('keydown', keepHistoryShortcutsInTextFields);
      }
      rootRef.current = node;
      if (node) {
        registerFormulaFocusSafeElement(apiRef, node);
        node.addEventListener('keydown', keepHistoryShortcutsInTextFields);
      }
    },
    [apiRef],
  );

  const closePanel = React.useCallback(() => {
    focusOpener(apiRef, openerRef.current);
    apiRef.current.hideComputedColumnEditor();
  }, [apiRef]);

  const showList = React.useCallback((focusField?: string | null) => {
    setView({ kind: 'list', focusField });
  }, []);

  // The editor session is keyed on open, not bound to the model. When the definition it
  // edits leaves the model under it (an undo, the API, the parent), applying or deleting
  // would have nothing to act on: the list takes over, and the panel keeps the focus
  // if it had it (the editor is gone). A changed definition keeps the draft.
  // The whole model is selected and the edited definition picked out here, instead of
  // passing `editedField` to `useGridSelector`: the field changes when the list opens
  // the editor, and under React 18 StrictMode the hook goes stale after a change of its
  // arguments, reporting the column as gone and sending the editor straight back.
  const model = useGridSelector(apiRef, gridComputedColumnsSelector);
  const editedField = view.kind === 'editor' ? view.session.definition?.field : undefined;
  const editedDefinition =
    editedField === undefined
      ? undefined
      : model.find((definition) => definition.field === editedField);
  const editedDefinitionGone = editedField !== undefined && editedDefinition === undefined;
  React.useEffect(() => {
    if (editedDefinitionGone) {
      const root = rootRef.current;
      const focusInside = root !== null && root.contains(root.ownerDocument.activeElement);
      showList(focusInside ? null : undefined);
    }
  }, [editedField, editedDefinitionGone, showList]);

  const openEditor = React.useCallback(
    (field: string | null) => {
      setView({
        kind: 'editor',
        key: nextEditorKey(),
        origin: 'list',
        session: {
          definition: field === null ? null : gridComputedColumnDefinitionSelector(apiRef, field),
        },
      });
    },
    [apiRef],
  );

  const handleEditorDone = React.useCallback(
    (outcome: GridComputedColumnsPanelEditorOutcome) => {
      if (view.kind !== 'editor') {
        return;
      }
      const appliedField = outcome.type === 'applied' ? outcome.field : null;
      // With a controlled model, the column only exists once the parent echoes the model.
      const appliedColumnExists =
        appliedField !== null && apiRef.current.getColumn(appliedField)?.computed === true;
      if (appliedColumnExists) {
        apiRef.current.scrollToIndexes({ colIndex: apiRef.current.getColumnIndex(appliedField) });
      }
      if (view.origin === 'list') {
        showList(appliedField ?? view.session.definition?.field ?? null);
        return;
      }
      if (appliedColumnExists) {
        apiRef.current.hideComputedColumnEditor();
        apiRef.current.setColumnHeaderFocus(appliedField);
        return;
      }
      closePanel();
    },
    [apiRef, view, showList, closePanel],
  );

  const handleBack = React.useCallback(() => {
    if (view.kind === 'editor') {
      showList(view.session.definition?.field ?? null);
    }
  }, [view, showList]);

  let titleKey:
    | 'computedColumnsPanelTitle'
    | 'computedColumnEditorNewTitle'
    | 'computedColumnEditorEditTitle'
    | 'computedColumnEditorViewTitle';
  if (view.kind === 'list') {
    titleKey = 'computedColumnsPanelTitle';
  } else if (view.session.definition === null) {
    titleKey = 'computedColumnEditorNewTitle';
  } else if (editedDefinition !== undefined && !isComputedColumnEditable(editedDefinition)) {
    titleKey = 'computedColumnEditorViewTitle';
  } else {
    titleKey = 'computedColumnEditorEditTitle';
  }
  const title = apiRef.current.getLocaleText(titleKey);

  return (
    <GridComputedColumnsPanelRoot
      ref={handleRootRef}
      ownerState={rootProps}
      className={classes.root}
      aria-labelledby={titleId}
    >
      <GridComputedColumnsPanelHeader
        title={title}
        titleId={titleId}
        onClose={closePanel}
        onBack={view.kind === 'editor' ? handleBack : undefined}
      />
      {view.kind === 'list' ? (
        <GridComputedColumnsPanelList
          onAdd={() => openEditor(null)}
          onEdit={openEditor}
          focusField={view.focusField}
        />
      ) : (
        <GridComputedColumnsPanelEditor
          key={view.key}
          session={view.session}
          onDone={handleEditorDone}
        />
      )}
    </GridComputedColumnsPanelRoot>
  );
}

export { GridComputedColumnsPanel };
